import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
  type ReactNode,
} from 'react'
import { auth, type MeResponse } from '../../api/auth'
import { ApiError } from '../../api/client'
import { kitLogin, kitRetryAfterSeconds } from '../../api/kitAuth'
import { useAuth } from '../../auth/AuthContext'
import { ClubMark } from '../../components/ClubMark'
import { PIN_LENGTH, PinDots, PinKeypad, PinStatus } from '../../components/PinPad'
import { KitDeviceScreen } from '../../kit/KitDeviceScreen'
import { clearKitAccess, loadKitAccess, type KitAccess } from '../../kit/kitAccessStore'
import { FALLBACK_LOCKOUT_S, useLockoutCountdown } from '../../kit/useLockoutCountdown'

/**
 * Entrada por DEFECTO de `/login`: el jugador con su código de 6 dígitos.
 *
 * El código identifica a un jugador DENTRO del club de un kit (no hay
 * endpoint global de login por PIN — decisión de seguridad, ver
 * `vrs-docs/15-backend-infra.md §13`), así que el dispositivo tiene que
 * saber a qué kit apunta:
 *
 *   - si ya hay binding (`kitAccessStore`, la misma key de localStorage que
 *     usan el modo tablet `/tablet` y el Quest), va derecho al pad con el
 *     branding del club;
 *   - si no, primero pide una única vez el "código del kit" (`KitCodeForm`,
 *     el mismo formulario de `/tablet` y del visor).
 *
 * Debajo del pad hay exactamente dos salidas: "Soy manager" (formulario
 * clásico email/password + Google) y "Olvidé mi código".
 */
export function PlayerLogin({
  onAuthenticated,
  onManager,
}: {
  onAuthenticated: (user: MeResponse) => void
  onManager: () => void
}) {
  const [access, setAccess] = useState<KitAccess | null>(() => loadKitAccess())
  const [forgotOpen, setForgotOpen] = useState(false)

  if (!access) {
    return (
      <PlayerFrame club={null}>
        <KitDeviceScreen
          onVerified={setAccess}
          className="panel max-w-xl p-2xl"
          footer={
            <div className="mt-lg flex justify-center">
              <SecondaryButton onClick={onManager}>Soy manager</SecondaryButton>
            </div>
          }
        />
      </PlayerFrame>
    )
  }

  return (
    <PlayerFrame club={access.club}>
      {forgotOpen ? (
        <ForgotCode onBack={() => setForgotOpen(false)} />
      ) : (
        <PlayerCodePad
          access={access}
          onAuthenticated={onAuthenticated}
          onManager={onManager}
          onForgot={() => setForgotOpen(true)}
          onChangeKit={() => {
            clearKitAccess()
            setAccess(null)
          }}
        />
      )}
    </PlayerFrame>
  )
}

/** Pad de 6 dígitos con auto-submit al último dígito. Espeja
 *  `kit/KitPinScreen.tsx` (mismo `PinPad`, mismo manejo de 401/404/429),
 *  pero al terminar navega según el rol en vez de seguir el ciclo de la
 *  tablet. */
function PlayerCodePad({
  access,
  onAuthenticated,
  onManager,
  onForgot,
  onChangeKit,
}: {
  access: KitAccess
  onAuthenticated: (user: MeResponse) => void
  onManager: () => void
  onForgot: () => void
  onChangeKit: () => void
}) {
  const { refresh } = useAuth()
  const [digits, setDigits] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [errorNonce, setErrorNonce] = useState(0)
  const { lockedFor, locked, startLockout } = useLockoutCountdown()

  const code = access.code

  const submit = useCallback(
    async (pin: string) => {
      setBusy(true)
      setError(null)
      try {
        await kitLogin(code, pin)
        // El backend setea la cookie en el mismo request; refresh() hidrata
        // /auth/me, que es de donde sale el destino post-login.
        const me = await refresh()
        if (!me) throw new Error('Entramos pero la sesión no se hidrató.')
        onAuthenticated(me)
      } catch (e) {
        setDigits('')
        setErrorNonce((n) => n + 1)
        const retry = kitRetryAfterSeconds(e)
        if (retry !== null || (e instanceof ApiError && e.status === 429)) {
          startLockout(retry ?? FALLBACK_LOCKOUT_S)
          setError(null)
        } else if (e instanceof ApiError && e.status === 401) {
          // Genérico a propósito: no distingue "ese código no existe" de "no
          // es el tuyo".
          setError('Código incorrecto.')
        } else if (e instanceof ApiError && e.status === 404) {
          setError('El código de tu club ya no está activo. Avisale al staff.')
        } else {
          setError(e instanceof Error ? e.message : 'Error de red.')
        }
      } finally {
        setBusy(false)
      }
    },
    [code, onAuthenticated, refresh],
  )

  // Igual que en la tablet: el submit sale del efecto y no del handler de la
  // tecla, así el sexto dígito alcanza a pintarse antes de la llamada.
  const submitted = useRef('')
  useEffect(() => {
    if (digits.length !== PIN_LENGTH || busy || locked) return
    if (submitted.current === digits) return
    submitted.current = digits
    void submit(digits)
  }, [digits, busy, locked, submit])

  const press = (key: number | 'delete') => {
    if (busy || locked) return
    setError(null)
    if (key === 'delete') {
      setDigits((d) => d.slice(0, -1))
      return
    }
    setDigits((d) => (d.length >= PIN_LENGTH ? d : d + String(key)))
  }

  return (
    <div className="panel max-w-xl p-2xl">
      <h1 className="m-0 text-2xl">Tu código (6 dígitos)</h1>
      <p className="mt-sm text-base text-text-secondary">
        El que configuraste desde el mail. Con eso entrás a entrenar.
      </p>

      <PinDots filled={digits.length} shakeKey={errorNonce} className="mt-lg" />

      {/* Mismo slot de alto fijo que la tablet: el mensaje no puede empujar
          el teclado mientras el jugador está tocando. */}
      <PinStatus tone={locked || error ? 'error' : 'muted'} className="mt-md">
        {locked
          ? `Demasiados intentos. Probá de nuevo en ${lockedFor} s.`
          : busy
            ? 'Verificando…'
            : error}
      </PinStatus>

      <PinKeypad onPress={press} disabled={busy || locked} className="mt-md" />

      <div className="mt-lg flex flex-wrap items-center justify-center gap-md">
        <SecondaryButton onClick={onManager}>Soy manager</SecondaryButton>
        <SecondaryButton onClick={onForgot}>Olvidé mi código</SecondaryButton>
      </div>

      {/* Rebinding del dispositivo, no logout: el binding es del aparato.
          Mismo criterio que "Desligar esta tablet" en la tablet. */}
      <div className="mt-md text-center">
        <button
          type="button"
          className="btn-ghost px-md py-sm text-xs text-text-muted"
          onClick={onChangeKit}
        >
          Cambiar de club
        </button>
      </div>
    </div>
  )
}

/**
 * "Olvidé mi código": el jugador tipea su mail y el backend le manda el link
 * de `/set-pin`. Usa `POST /auth/pin/reset-request` (siempre 200), no la
 * variante por kit — desde `/login` el jugador puede estar en su casa, sin
 * el kit a mano. La copia es la misma no-comprometedora de la tablet: nunca
 * revela si ese mail existe.
 */
function ForgotCode({ onBack }: { onBack: () => void }) {
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!email.includes('@') || busy) return
    setBusy(true)
    try {
      await auth.pinResetRequest(email.trim().toLowerCase())
    } catch {
      // Siempre 200 en el happy path; un error de red no cambia la copia,
      // porque reintentar tampoco puede revelar si ese mail es de un jugador.
    } finally {
      setBusy(false)
      setSent(true)
    }
  }

  return (
    <div className="panel max-w-xl p-2xl">
      <h1 className="m-0 text-2xl">Olvidé mi código</h1>
      {sent ? (
        <>
          <div className="banner-ok mt-lg">
            Si el mail corresponde a un jugador, te llega un link para
            configurar uno nuevo.
          </div>
          <button type="button" className="btn-primary mt-lg w-full py-lg text-lg" onClick={onBack}>
            Volver
          </button>
        </>
      ) : (
        <>
          <p className="mt-sm text-base text-text-secondary">
            Escribí tu mail y te mandamos un link para configurar uno nuevo.
          </p>
          <form onSubmit={onSubmit} className="mt-lg flex flex-col gap-md">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@dominio.com"
              autoComplete="email"
              autoFocus
              disabled={busy}
              className="input py-md text-lg"
            />
            <button
              type="submit"
              disabled={!email.includes('@') || busy}
              className="btn-primary py-lg text-lg disabled:opacity-50"
            >
              {busy ? 'Enviando…' : 'Mandarme el link'}
            </button>
            <button type="button" className="btn-ghost py-md" onClick={onBack}>
              Volver
            </button>
          </form>
        </>
      )}
    </div>
  )
}

function SecondaryButton({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" className="btn px-lg py-sm text-sm" onClick={onClick}>
      {children}
    </button>
  )
}

/**
 * Contenedor con el branding del club en variables CSS locales (mismo
 * patrón que `KitLayout`: viven en el subárbol, no en `:root`, así no pelean
 * con el efecto de branding del AuthContext) + la cabecera con logo y
 * nombre, para que el jugador vea que está entrando por su club.
 */
function PlayerFrame({
  club,
  children,
}: {
  club: KitAccess['club'] | null
  children: ReactNode
}) {
  const primary = club?.primaryColor ?? '#E25A1B'
  const style: CSSProperties = {
    ['--club-primary' as string]: primary,
    ['--club-secondary' as string]: club?.secondaryColor ?? primary,
    ['--club-accent' as string]: club?.accentColor ?? primary,
  }
  return (
    <div style={style} className="flex w-full flex-col items-center gap-lg">
      {club && (
        <div className="flex items-center gap-md">
          <ClubMark logoUrl={club.logoUrl} name={club.name} size={40} />
          <span className="text-lg font-bold text-text-primary">{club.name}</span>
        </div>
      )}
      {children}
    </div>
  )
}
